import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import MultiselectCheck from "../../Componentes/Utils/MultiselectCheck";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";

const PAGINA = 100;

const matriculaParaNumero = (matricula) =>
    parseInt(String(matricula ?? "").replace(/\D/g, ""), 10);

const Title = styled.h3`
    margin: 0 0 16px 0;
    padding: 0 0 12px 0;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
`;

const Filtros = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 14px;
`;

const RangeMatricula = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const InputMatricula = styled.input`
    width: 100%;
    height: 38px;
    box-sizing: border-box;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    padding: 0 10px;
    font-size: 14px;
    font-family: inherit;
    color: #1f2937;
    background-color: #fff;

    &::placeholder {
        color: #666;
    }

    &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
    }
`;

const SeparadorRange = styled.span`
    font-size: 13px;
    color: #6b7280;
    flex-shrink: 0;
`;

const ListaWrapper = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: auto;
    max-height: 45dvh;
`;

const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
`;

const Thead = styled.thead`
    th {
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #f3f4f6;
        text-align: left;
        padding: 8px 10px;
        font-weight: 600;
        color: #374151;
        white-space: nowrap;
    }

    th.check {
        width: 42px;
        text-align: center;
    }
`;

const Linha = styled.tr`
    cursor: pointer;
    border-top: 1px solid #f1f1f1;
    background-color: ${({ $selected }) => ($selected ? "#e6f7ff" : "#fff")};

    &:hover {
        background-color: ${({ $selected }) => ($selected ? "#d6f0ff" : "#f9fafb")};
    }

    td {
        padding: 8px 10px;
        color: #1f2937;
    }

    td.check {
        width: 42px;
        text-align: center;
    }
`;

const Vazio = styled.p`
    margin: 0;
    padding: 24px 10px;
    text-align: center;
    color: #6b7280;
    font-size: 13px;
`;

const Rodape = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    gap: 12px;
`;

const Contador = styled.span`
    font-size: 13px;
    color: #4b5563;
`;

const BotaoContinuar = styled.button`
    padding: 9px 18px;
    background-color: #007bff;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #0056b3;
    }

    &:disabled {
        background-color: #9bc4ec;
        cursor: not-allowed;
    }
`;

const ModalGerarContratos = ({ colabs = [], onContinuar }) => {

    const { closeModal } = useModal();
    const { notifyWarning } = useToast();

    const [matriculaDe, setMatriculaDe] = useState("");
    const [matriculaAte, setMatriculaAte] = useState("");
    const [centrosSelecionados, setCentrosSelecionados] = useState([]);
    const [matriculasSelecionadas, setMatriculasSelecionadas] = useState([]);
    const [limite, setLimite] = useState(PAGINA);

    const listaRef = useRef(null);

    const opcoesCentro = useMemo(() => {
        const centros = [
            ...new Set(
                colabs
                    .map((colab) => colab.CENTRO_CUSTO)
                    .filter((centro) => centro)
            ),
        ].sort();

        return centros.map((centro) => ({ value: centro, label: centro }));
    }, [colabs]);

    const colabsOrdenados = useMemo(() => {
        return [...colabs].sort(
            (a, b) => matriculaParaNumero(a.MATRICULA) - matriculaParaNumero(b.MATRICULA)
        );
    }, [colabs]);

    const colabsFiltrados = useMemo(() => {
        const de = matriculaDe.trim() ? matriculaParaNumero(matriculaDe) : null;
        const ate = matriculaAte.trim() ? matriculaParaNumero(matriculaAte) : null;

        return colabsOrdenados.filter((colab) => {
            const matNumero = matriculaParaNumero(colab.MATRICULA);

            const deFiltro = de === null || (!Number.isNaN(matNumero) && matNumero >= de);
            const ateFiltro = ate === null || (!Number.isNaN(matNumero) && matNumero <= ate);

            const centroFiltro =
                centrosSelecionados.length === 0 ||
                centrosSelecionados.includes(colab.CENTRO_CUSTO);

            return deFiltro && ateFiltro && centroFiltro;
        });
    }, [colabsOrdenados, matriculaDe, matriculaAte, centrosSelecionados]);

    const colabsVisiveis = useMemo(
        () => colabsFiltrados.slice(0, limite),
        [colabsFiltrados, limite]
    );

    useEffect(() => {
        setLimite(PAGINA);
        if (listaRef.current) listaRef.current.scrollTop = 0;
    }, [matriculaDe, matriculaAte, centrosSelecionados]);

    const handleScroll = () => {
        const el = listaRef.current;
        if (!el) return;

        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
            setLimite((prev) =>
                prev < colabsFiltrados.length ? prev + PAGINA : prev
            );
        }
    };

    const todosFiltradosSelecionados =
        colabsFiltrados.length > 0 &&
        colabsFiltrados.every((colab) =>
            matriculasSelecionadas.includes(colab.MATRICULA)
        );

    const toggleColaborador = (matricula) => {
        setMatriculasSelecionadas((prev) =>
            prev.includes(matricula)
                ? prev.filter((item) => item !== matricula)
                : [...prev, matricula]
        );
    };

    const toggleTodos = () => {
        const matriculasFiltradas = colabsFiltrados.map((colab) => colab.MATRICULA);

        if (todosFiltradosSelecionados) {
            setMatriculasSelecionadas((prev) =>
                prev.filter((item) => !matriculasFiltradas.includes(item))
            );
        } else {
            setMatriculasSelecionadas((prev) => [
                ...new Set([...prev, ...matriculasFiltradas]),
            ]);
        }
    };

    const continuar = () => {
        const selecionados = colabs.filter((colab) =>
            matriculasSelecionadas.includes(colab.MATRICULA)
        );

        if (selecionados.length === 0) {
            notifyWarning("Selecione ao menos um colaborador.");
            return;
        }

        onContinuar(selecionados);
    };

    return (
        <Overlay state changeContent={closeModal}>
            <Modal header close={closeModal} size="lg">
                <Title>GERAR CONTRATOS — SELECIONE OS COLABORADORES</Title>

                <Filtros>
                    <RangeMatricula>
                        <InputMatricula
                            type="text"
                            inputMode="numeric"
                            placeholder="MATRÍCULA DE..."
                            value={matriculaDe}
                            onChange={(e) =>
                                setMatriculaDe(e.target.value.replace(/\D/g, ""))
                            }
                        />
                        <SeparadorRange>até</SeparadorRange>
                        <InputMatricula
                            type="text"
                            inputMode="numeric"
                            placeholder="MATRÍCULA ATÉ..."
                            value={matriculaAte}
                            onChange={(e) =>
                                setMatriculaAte(e.target.value.replace(/\D/g, ""))
                            }
                        />
                    </RangeMatricula>
                    <MultiselectCheck
                        options={opcoesCentro}
                        selectedOptions={centrosSelecionados}
                        setSelectedOptions={setCentrosSelecionados}
                        placeholder="CENTRO DE CUSTO..."
                    />
                </Filtros>

                <ListaWrapper ref={listaRef} onScroll={handleScroll}>
                    <Tabela>
                        <Thead>
                            <tr>
                                <th className="check">
                                    <input
                                        type="checkbox"
                                        checked={todosFiltradosSelecionados}
                                        onChange={toggleTodos}
                                    />
                                </th>
                                <th>COLABORADOR</th>
                                <th>MATRÍCULA</th>
                                <th>FUNÇÃO</th>
                                <th>CENTRO CUSTO</th>
                            </tr>
                        </Thead>
                        <tbody>
                            {colabsFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan={5}>
                                        <Vazio>NENHUM COLABORADOR ENCONTRADO</Vazio>
                                    </td>
                                </tr>
                            ) : (
                                colabsVisiveis.map((colab) => {
                                    const selecionado = matriculasSelecionadas.includes(
                                        colab.MATRICULA
                                    );

                                    return (
                                        <Linha
                                            key={colab.MATRICULA}
                                            $selected={selecionado}
                                            onClick={() =>
                                                toggleColaborador(colab.MATRICULA)
                                            }
                                        >
                                            <td className="check">
                                                <input
                                                    type="checkbox"
                                                    checked={selecionado}
                                                    readOnly
                                                />
                                            </td>
                                            <td>{colab.NOME}</td>
                                            <td>{colab.MATRICULA}</td>
                                            <td>{colab.FUNCAO ?? "-"}</td>
                                            <td>{colab.CENTRO_CUSTO ?? "-"}</td>
                                        </Linha>
                                    );
                                })
                            )}
                        </tbody>
                    </Tabela>
                </ListaWrapper>

                <Rodape>
                    <Contador>
                        {matriculasSelecionadas.length} colaborador
                        {matriculasSelecionadas.length === 1 ? "" : "es"} selecionado
                        {matriculasSelecionadas.length === 1 ? "" : "s"}
                    </Contador>
                    <BotaoContinuar
                        onClick={continuar}
                        disabled={matriculasSelecionadas.length === 0}
                    >
                        CONTINUAR
                    </BotaoContinuar>
                </Rodape>
            </Modal>
        </Overlay>
    );
};

export default ModalGerarContratos;
